import {API_BASE} from "./reports";
import buildHeaders from './fetchHeaders';

function extractErrorMessage(payload) {
    if (!payload) return '';
    if (typeof payload === 'string') return payload.trim();
    if (typeof payload?.message === 'string' && payload.message.trim()) return payload.message.trim();
    if (typeof payload?.error === 'string' && payload.error.trim()) return payload.error.trim();
    if (typeof payload?.error?.message === 'string' && payload.error.message.trim()) return payload.error.message.trim();
    return '';
}

async function readResponsePayload(response) {
    const contentType = response?.headers?.get?.('content-type') || '';

    if (contentType.includes('application/json')) {
        const data = await response.json().catch(() => null);
        return {data, message: extractErrorMessage(data)};
    }

    const text = await response.text().catch(() => '');
    if (!text) return {data: null, message: ''};

    try {
        const data = JSON.parse(text);
        return {data, message: extractErrorMessage(data) || text.trim()};
    } catch (_) {
        return {data: text, message: text.trim()};
    }
}

export async function apiCreateTag(tag) {

    const url = `${API_BASE}/tags`;

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: buildHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            }),
            body: JSON.stringify(tag),
        });


        const {data, message} = await readResponsePayload(response);

        if (!response.ok) {
            const detail = message || `${response.status} ${response.statusText}`.trim();
            const err = new Error(`Failed to create tag: ${detail}`);
            // include response details for callers
            err.response = response;
            err.data = data;
            throw err;
        }

        return data;
    } catch (error) {
        console.error('apiCreateTag error:', error);
        throw error;
    }
}

// New: search tags by query; if query is empty, backend returns top 5
export async function apiSearchTags(search = '', docId) {
    // build query params including optional docId
    const params = new URLSearchParams();
    // Always include search parameter (even if empty) - backend may require it
    params.set('search', search || '');
    const qs = params.toString();
    const url = `${API_BASE}/tags${qs ? `?${qs}` : ''}`;

    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: buildHeaders({
                'Accept': 'application/json',
            }),
        });

        const data = await response.json().catch(() => null);

        if (!response.ok) {
            const err = new Error(`Failed to search tags: ${response.status} ${response.statusText}`);
            err.response = response;
            err.data = data;
            throw err;
        }

        // Data could be an array of strings or objects; return as-is and let callers normalize
        return Array.isArray(data) ? data : [];
    } catch (error) {
        console.error('apiSearchTags error:', error);
        throw error;
    }
}

// DiTag catalog autocomplete (data_item_tag) — the My Data / Highlights parallel of
// apiSearchTags (corpus catalog). Returns [{id, name, color}].
export async function apiSearchDiTags(search = '', limit = 20) {
    const params = new URLSearchParams();
    params.set('search', search || '');
    params.set('limit', String(limit));
    const url = `${API_BASE}/data-item-tags?${params}`;
    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: buildHeaders({'Accept': 'application/json'}),
        });
        const data = await response.json().catch(() => null);
        if (!response.ok) {
            const err = new Error(`Failed to search DiTags: ${response.status} ${response.statusText}`);
            err.response = response;
            err.data = data;
            throw err;
        }
        return Array.isArray(data) ? data : [];
    } catch (error) {
        console.error('apiSearchDiTags error:', error);
        throw error;
    }
}

// New: get tags for a specific segment (docId + segmentId + optional offset)
export async function apiGetSegmentTags({docId, segmentId, offset}) {
    const params = new URLSearchParams();
    if (docId) params.set('docId', docId);
    if (segmentId) params.set('segmentId', segmentId);
    if (typeof offset !== 'undefined' && offset !== null) params.set('offset', String(offset));
    // const url = `${API_BASE}/tags/segment?${params.toString()}`;
    const url = `${API_BASE}/tags?${params.toString()}`;

    const response = await fetch(url, {
        method: 'GET',
        headers: buildHeaders({
            'Accept': 'application/json',
        }),
    });

    if (!response.ok) {
        let message = 'Failed to fetch segment tags';
        try {
            const errText = await response.text();
            if (errText) message += `: ${errText}`;
        } catch (_) {
        }
        throw new Error(message);
    }

    try {
        const data = await response.json();
        return Array.isArray(data) ? data : [];
    } catch (_) {
        return [];
    }
}

export async function apiDeleteTag(operationId) {
    const url = `${API_BASE}/tags/by-operation-id/${operationId}`;

    const response = await fetch(url, {
        method: 'DELETE',
        headers: buildHeaders({
            'Accept': 'application/json',
        }),
    });

    if (!response.ok) {
        let message = 'Failed to delete tag';
        try {
            const errText = await response.text();
            if (errText) message += `: ${errText}`;
        } catch (_) {
        }
        throw new Error(message);
    }

    return response;

}

// New: request auto-tag suggestions by searchId
export async function apiSuggestTags({searchId, includeNewSuggestions = true}, {signal} = {}) {
    if (!searchId) throw new Error('searchId is required');
    const url = `${API_BASE}/auto-tag/suggest`;
    const response = await fetch(url, {
        method: 'POST',
        headers: buildHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        }),
        body: JSON.stringify({searchId, includeNewSuggestions}),
        signal,
    });

    if (!response.ok) {
        let message = 'Failed to fetch tag suggestions';
        try {
            const errText = await response.text();
            if (errText) message += `: ${errText}`;
        } catch (_) {
        }
        throw new Error(message);
    }

    const data = await response.json();
    // expect shape { segments: [ { segmentId, existingTags: [], newTags: [] } ] }
    return data && typeof data === 'object' ? data : {segments: []};
}

// Suggest tags from raw text (used for report-segment / data-item source cards that
// have no corpus segmentId). The text path of /api/auto-tag/suggest returns suggestions
// at the TOP level (existingTags/newTags), not under segments[], so we normalize here to
// the same { existingTags, newTags } shape the segment path yields.
export async function apiSuggestTagsByText({text, includeNewSuggestions = true}, {signal} = {}) {
    const trimmed = (text || '').toString().trim();
    if (!trimmed) return {existingTags: [], newTags: []};
    const url = `${API_BASE}/auto-tag/suggest`;
    const response = await fetch(url, {
        method: 'POST',
        headers: buildHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        }),
        body: JSON.stringify({text: trimmed, includeNewSuggestions}),
        signal,
    });

    if (!response.ok) {
        let message = 'Failed to fetch tag suggestions';
        try {
            const errText = await response.text();
            if (errText) message += `: ${errText}`;
        } catch (_) {
        }
        throw new Error(message);
    }

    const data = await response.json();
    return {
        existingTags: Array.isArray(data?.existingTags) ? data.existingTags : [],
        newTags: Array.isArray(data?.newTags) ? data.newTags : [],
    };
}

// ── Unified-search card tagging: shared helpers ─────────────────────────────

async function requestJson(url, {method = 'GET', body, signal} = {}) {
    const headers = buildHeaders(
        body !== undefined
            ? {'Content-Type': 'application/json', 'Accept': 'application/json'}
            : {'Accept': 'application/json'});
    const response = await fetch(url, {
        method,
        headers,
        ...(body !== undefined ? {body: JSON.stringify(body)} : {}),
        signal,
    });
    const {data, message} = await readResponsePayload(response);
    if (!response.ok) {
        const detail = message || `${response.status} ${response.statusText}`.trim();
        const err = new Error(`Request failed: ${detail}`);
        err.response = response;
        err.data = data;
        throw err;
    }
    return data;
}

// ── Products (report-segment) tags — entity_tags keyed by report_segments.segment_id ──

export async function apiGetReportSegmentTags({reportSegmentId, userId}) {
    const params = new URLSearchParams();
    if (userId) params.set('user', userId);
    const qs = params.toString();
    const data = await requestJson(
        `${API_BASE}/tags/report-segments/${reportSegmentId}${qs ? `?${qs}` : ''}`);
    return Array.isArray(data) ? data : [];
}

export async function apiTagReportSegment({reportSegmentId, operationId, name, userId}) {
    return requestJson(`${API_BASE}/tags/report-segments/${reportSegmentId}`, {
        method: 'POST',
        body: {operationId, name, userId},
    });
}

export async function apiRemoveReportSegmentTag({reportSegmentId, tagName, userId}) {
    const params = new URLSearchParams();
    params.set('userId', userId || '');
    return requestJson(
        `${API_BASE}/tags/report-segments/${reportSegmentId}/${encodeURIComponent(tagName)}?${params}`,
        {method: 'DELETE'});
}

// ── My Data segment tags — DiTag links on data_item_segment_tag_link (toggle, no votes) ──

export async function apiGetDataItemSegmentTags({segmentId, userId}) {
    const params = new URLSearchParams();
    if (userId) params.set('userId', userId);
    const qs = params.toString();
    const data = await requestJson(
        `${API_BASE}/data-items/segments/${segmentId}/tags${qs ? `?${qs}` : ''}`);
    return Array.isArray(data) ? data : [];
}

export async function apiAddDataItemSegmentTag({segmentId, name, userId}) {
    const params = new URLSearchParams();
    if (userId) params.set('userId', userId);
    const qs = params.toString();
    const data = await requestJson(
        `${API_BASE}/data-items/segments/${segmentId}/tags${qs ? `?${qs}` : ''}`,
        {method: 'POST', body: {name}});
    return Array.isArray(data) ? data : [];
}

export async function apiRemoveDataItemSegmentTag({segmentId, name, userId}) {
    const params = new URLSearchParams();
    if (userId) params.set('userId', userId);
    const qs = params.toString();
    const data = await requestJson(
        `${API_BASE}/data-items/segments/${segmentId}/tags/${encodeURIComponent(name)}${qs ? `?${qs}` : ''}`,
        {method: 'DELETE'});
    return Array.isArray(data) ? data : [];
}

// Model-assisted DiTag suggestions for a My Data sentence segment (the My Data path of the
// search-card "Suggested tags" strip). Returns [{tagId, name, confidence, newTag}] which we
// normalize to the {existingTags, newTags} shape SuggestedTags expects.
export async function apiSuggestDataItemSegmentTags({segmentId, userId}, {signal} = {}) {
    const params = new URLSearchParams();
    if (userId) params.set('userId', userId);
    const qs = params.toString();
    const data = await requestJson(
        `${API_BASE}/data-items/segments/${segmentId}/tags/suggest${qs ? `?${qs}` : ''}`,
        {method: 'POST', body: {}, signal});
    const items = Array.isArray(data) ? data : [];
    return {
        existingTags: items.filter(t => !t?.newTag).map(t => ({name: t.name, confidence: t.confidence})),
        newTags: items.filter(t => !!t?.newTag).map(t => ({name: t.name, confidence: t.confidence})),
    };
}

// ── Highlights item-level tags — DiTag links on data_item_tag_link (toggle, no votes) ──

export async function apiAddDataItemTag({dataItemId, name, userId}) {
    const params = new URLSearchParams();
    if (userId) params.set('userId', userId);
    const qs = params.toString();
    const data = await requestJson(
        `${API_BASE}/data-items/${dataItemId}/tags${qs ? `?${qs}` : ''}`,
        {method: 'POST', body: {name}});
    return Array.isArray(data) ? data : [];
}

export async function apiRemoveDataItemTag({dataItemId, name, userId}) {
    const params = new URLSearchParams();
    if (userId) params.set('userId', userId);
    const qs = params.toString();
    const data = await requestJson(
        `${API_BASE}/data-items/${dataItemId}/tags/${encodeURIComponent(name)}${qs ? `?${qs}` : ''}`,
        {method: 'DELETE'});
    return Array.isArray(data) ? data : [];
}

// ── Unified filter autocomplete — deduped names across both tag catalogs ──

export async function apiSearchUnifiedTags(search = '', limit = 20) {
    const params = new URLSearchParams();
    params.set('search', search || '');
    params.set('limit', String(limit));
    const data = await requestJson(`${API_BASE}/search/tags?${params}`);
    return Array.isArray(data) ? data : [];
}

export async function apiSuggestTagsBySegmentId({segmentId, includeNewSuggestions = true}, {signal} = {}) {
    if (!segmentId) throw new Error('segmentId is required');
    const url = `${API_BASE}/auto-tag/suggest`;
    const response = await fetch(url, {
        method: 'POST',
        headers: buildHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        }),
        body: JSON.stringify({segmentIds: [segmentId], includeNewSuggestions}),
        signal,
    });

    if (!response.ok) {
        let message = 'Failed to fetch tag suggestions';
        try {
            const errText = await response.text();
            if (errText) message += `: ${errText}`;
        } catch (_) {
        }
        throw new Error(message);
    }

    const data = await response.json();
    // expect shape { segments: [ { segmentId, existingTags: [], newTags: [] } ] }
    return data && typeof data === 'object' ? data : {segments: []};
}
